import { closeAllPopups, closePopupsFor } from "@/lib/push";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { ErrorState, PageHeader, ViewsPanel } from "@/components/patterns";
import { Archive, AtSign, Bell, CheckCheck, CircleUser, ClipboardCheck, Eye, Inbox, PhoneCall, Settings, Undo2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { NotificationItem } from "@/types/notification";
import { cn } from "@/lib/utils";
import { dateLocale, withClock } from "@/lib/dateTime";

type Counts = Record<string, number>;
type Row = NotificationItem & { archived_at?: string | null };

/** What a notification is about, for its icon and colour */
const KIND: Record<string, { icon: React.ComponentType<{ className?: string }>; tint: string }> = {
  mentions: { icon: AtSign, tint: "bg-accent text-primary" },
  approvals: { icon: ClipboardCheck, tint: "bg-warning-soft text-warning" },
  customer: { icon: Eye, tint: "bg-success-soft text-success" },
  reminders: { icon: Bell, tint: "bg-danger-soft text-danger" },
  assigned: { icon: CircleUser, tint: "bg-muted text-foreground" },
  calls: { icon: PhoneCall, tint: "bg-info-soft text-info" },
  other: { icon: Bell, tint: "bg-accent text-primary" },
};
const KIND_OF_TYPE: Record<string, keyof typeof KIND> = {
  MENTION: "mentions",
  LEAD_ASSIGNED: "assigned", DEAL_CREATED: "assigned", DEAL_ASSIGNED: "assigned", TASK_ASSIGNED: "assigned",
  DEAL_WON: "customer", DEAL_LOST: "customer",
  QUOTE_APPROVAL: "approvals", QUOTE_DECIDED: "approvals", PROPOSAL_REVIEW: "approvals", ACCESS_REQUEST: "approvals",
  MISSED_CALL: "calls", CALLBACK_DUE: "calls", CALL_INFO: "calls", CALL_REVIEW: "calls", HOT_LEAD_UNCALLED: "calls",
  PROPOSAL_VIEWED: "customer", PROPOSAL_ACCEPTED: "customer", PROPOSAL_DECLINED: "customer", PROPOSAL_EXPIRED: "customer", WHATSAPP: "customer", EMAIL_REPLY: "customer", EMAIL_UNLINKED: "customer",
  TASK_REMINDER: "reminders", MEETING_REMINDER: "reminders", PROPOSAL_FOLLOW_UP: "reminders", PROPOSAL_EXPIRING: "reminders", DEAL_STAGE_OVERDUE: "reminders", LEAD_UNTOUCHED: "reminders",
};

const VIEW_TITLE: Record<string, string> = {
  all: "All",
  unread: "Unread",
  mentions: "Mentions",
  assigned: "Assigned to me",
  approvals: "Approvals",
  customer: "Customer activity",
  reminders: "Reminders",
  calls: "Calls",
  archive: "Archive",
};

const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
function dayLabel(iso: string) {
  const diff = Math.round((dayStart(new Date()) - dayStart(new Date(iso))) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "long", year: "numeric" });
}
const clock = (iso: string) => new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));

export function NotificationsPage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [view, setView] = useState("all");
  const [counts, setCounts] = useState<Counts>({});

  const loadCounts = () =>
    api
      .get("/api/Notification/Counts")
      .then((res) => setCounts(unwrapObject<Counts>(res)))
      .catch(() => undefined);

  const list = usePagedList<Row>({
    deps: [view],
    load: async (page) => {
      try {
        return unwrapPage<Row>(await api.get(`/api/Notification/Feed?view=${view}&page=${page}&page_size=30`));
      } catch (err: any) {
        throw err;
      }
    },
  });

  useEffect(() => {
    loadCounts();
  }, []);

  const refresh = () => Promise.all([list.reload(), loadCounts()]);

  // A push just arrived while this page is open
  useEffect(() => {
    const onNew = () => void refresh();
    window.addEventListener("crm:notification", onNew);
    return () => window.removeEventListener("crm:notification", onNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const open = async (n: Row) => {
    if (!n.read_at) {
      await api.patch(`/api/Notification/MarkRead/${n.id}`).catch(() => undefined);
      void closePopupsFor([n]);
      window.dispatchEvent(new Event("crm:unread-changed"));
      loadCounts();
    }
    if (n.link) navigate(n.link);
    else list.reload();
  };

  const markAll = async () => {
    try {
      await api.patch("/api/Notification/MarkAllRead");
      void closeAllPopups();
      window.dispatchEvent(new Event("crm:unread-changed"));
      await refresh();
    } catch (err: any) {
      error("Could not mark read", err.message || "Please try again");
    }
  };

  const archive = async (n: Row, put: boolean) => {
    try {
      await api.patch(`/api/Notification/${put ? "Archive" : "Unarchive"}/${n.id}`);
      if (put) void closePopupsFor([n]);
      window.dispatchEvent(new Event("crm:unread-changed"));
      await refresh();
    } catch (err: any) {
      error("Could not update", err.message || "Please try again");
    }
  };

  const groups = useMemo(() => {
    const out: { label: string; items: Row[] }[] = [];
    for (const n of list.rows) {
      const label = dayLabel(n.created_on);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(n);
      else out.push({ label, items: [n] });
    }
    return out;
  }, [list.rows]);

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Notifications"
        subtitle="What needs you"
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "all", label: "All", count: counts.all },
              { key: "mentions", label: "Mentions", count: counts.mentions },
              { key: "assigned", label: "Assigned to me", count: counts.assigned },
              { key: "approvals", label: "Approvals", count: counts.approvals },
              { key: "customer", label: "Customer activity", count: counts.customer },
              { key: "reminders", label: "Reminders", count: counts.reminders },
              { key: "calls", label: "Calls", count: counts.calls },
              { key: "archive", label: "Archive", count: counts.archive || "" },
            ],
          },
        ]}
      />

      <PageHeader
        title="Notifications"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings/notifications">
                <Settings className="mr-1.5 size-4" />
                Notification settings
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={markAll} disabled={!counts.all}>
              <CheckCheck className="mr-1.5 size-4" />
              Mark all read
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-start gap-4">
        <main className="flex min-w-0 flex-[999_1_520px] flex-col gap-3">
          <div className="flex items-baseline gap-2.5">
            <h1 className="text-[19px] font-semibold text-foreground">{VIEW_TITLE[view]}</h1>
            {view !== "archive" && <span className="text-muted-foreground">{counts[view] ?? counts.all ?? 0} unread</span>}
          </div>

          {!!list.error && <ErrorState title="Couldn't load your notifications" onRetry={list.reload} error={list.error} />}
          {!list.loading && !list.error && groups.length === 0 && (
            <div className="rounded-[10px] border border-border bg-card py-14 text-center text-muted-foreground">
              <Inbox className="mx-auto mb-2 size-9 opacity-40" />
              <p className="font-medium text-foreground">{view === "archive" ? "Nothing archived" : "You’re all caught up"}</p>
              <p className="text-xs">{view === "archive" ? "Archived notifications show up here." : "New activity on your leads, deals and proposals appears here."}</p>
            </div>
          )}

          {groups.map((g) => (
            <section key={g.label} className="flex flex-col gap-1.5">
              <h2 className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">{g.label}</h2>
              <div className="overflow-hidden rounded-[10px] border border-border bg-card">
                {g.items.map((n, i) => {
                  const k = KIND[KIND_OF_TYPE[n.type] || "other"] as (typeof KIND)[string];
                  const Icon = k.icon;
                  return (
                    <div key={n.id} className={cn("flex items-start gap-3 px-3.5 py-3", i > 0 && "border-t border-border/60", !n.read_at && view !== "archive" && "bg-accent/40")}>
                      <span className={cn("mt-2.5 size-2 shrink-0 rounded-full", n.read_at ? "bg-transparent" : "bg-primary")} aria-label={n.read_at ? undefined : "Unread"} />
                      <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-lg", k.tint)}>
                        <Icon className="size-3.5" />
                      </span>
                      <button type="button" onClick={() => open(n)} className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                        <span className={cn("text-[13px]", !n.read_at ? "font-semibold text-foreground" : "font-medium text-foreground/80")}>{n.title}</span>
                        {n.body && <span className="line-clamp-2 text-[12.5px] text-muted-foreground">{n.body}</span>}
                      </button>
                      <span className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className="font-mono text-[11.5px] text-muted-foreground">{clock(n.created_on)}</span>
                        <button type="button" onClick={() => archive(n, view !== "archive")} className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground" title={view === "archive" ? "Bring back" : "Archive"}>
                          {view === "archive" ? <Undo2 className="size-3" /> : <Archive className="size-3" />}
                          {view === "archive" ? "Restore" : "Archive"}
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </main>

      </div>
    </div>
  );
}
